import {useCallback} from 'react';
import {useLatest} from 'ahooks';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {handleDragDropFilesUpload, retryPendingUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';

export interface UseAiPromptBoxDragDropUploader {
  handleFiles: (files: File[]) => void;
  retryUpload: (localId: string) => void;
  showInvalidFileTypeError: VoidFunction;
}

export const useAiPromptBoxDragDropUploader = (id: string): UseAiPromptBoxDragDropUploader => {
  const {state, actions} = useAiPromptBox(id);
  const attachmentsRef = useLatest(state.attachments);

  const showInvalidFileTypeError = useCallback((): void => {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_ai_chat_unsupported_file_type'),
      showCloseIcon: true,
      interval: 7000,
    });
  }, []);

  const buildUploadState = useCallback(() => ({
    promptBoxId: id,
    getAttachmentCount: (): number => attachmentsRef.current?.items.length ?? 0,
    setActive: actions.setActiveIfNotProcessing,
    removeImageAttachments: actions.removeImageAttachments,
    addPendingImageAttachments: actions.addPendingImageAttachments,
    resolvePendingAttachment: actions.resolvePendingAttachment,
    markPendingAttachmentFailed: actions.markPendingAttachmentFailed,
    markPendingAttachmentUploading: actions.markPendingAttachmentUploading,
  }), [id, attachmentsRef, actions]);

  const handleFiles = useCallback(
    (files: File[]): void => {
      handleDragDropFilesUpload(files, buildUploadState(), showInvalidFileTypeError);
    },
    [buildUploadState, showInvalidFileTypeError]
  );

  const retryUpload = useCallback(
    (localId: string): void => {
      retryPendingUpload(localId, buildUploadState());
    },
    [buildUploadState]
  );

  return {handleFiles, retryUpload, showInvalidFileTypeError};
};
